import React, {
  type ChangeEvent,
  forwardRef,
  type RefObject,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibCheckbox, KibRadio } from '@chewy/kib-choices-react'
import { KibField, KibSelect, KibTextarea } from '@chewy/kib-fields-new-react'
import { KibInputGroup } from '@chewy/kib-fields-react'
import { Tooltip } from '@mui/material'
import classNames from 'classnames'
import { type Field, Utils } from '@pbt/pbt-ui-components'

import UserSelect from '~/components/common/inputs/UserSelect'
import useTeamMembers from '~/components/common/inputs/useTeamMembers'
import DoctorLicenseStateWarning from '~/components/dashboard/prescription/prescription-editor/DoctorLicenseStateWarning'
import DoctorLicenseWarning from '~/components/dashboard/prescription/prescription-editor/DoctorLicenseWarning'
import { useCanApproveAndSubmitChewyActiveRx } from '~/components/dashboard/prescription/prescription-editor/hooks/useCanApproveAndSubmitChewyActiveRx'
import PrescriptionChewyOrderInfo from '~/components/dashboard/prescription/prescription-editor/PrescriptionChewyOrderInfo'
import QuantityAndRefills from '~/components/dashboard/prescription/prescription-editor/QuantityAndRefills'
import SwapProductButtons from '~/components/dashboard/prescription/prescription-editor/SwapProductButtons'
import { TaskFormValidationErrorToast } from '~/components/dashboard/tasks-dashboard/TaskFormValidationErrorToast'
import FeatureToggle from '~/constants/featureToggle'
import {
  AdministrationCourseType,
  AdministrationCourseTypeStr,
  type PrescriptionStates,
  PrescriptionType,
} from '~/constants/prescription'
import RoleName from '~/constants/roleNames'
import { fetchMembersForSelectList } from '~/store/actions/members'
import { usePrescriptionAdminInstructions } from '~/store/hooks/orders'
import { useGetIsCompoundingReasonDetailsRequired } from '~/store/hooks/prescription'
import { useMainStaffRoles } from '~/store/hooks/useMainStaffRoles'
import {
  getDrugAdministrationFrequencies,
  getDrugDeliveryMethod,
  getFeatureToggle,
  getPrescriptionProductSizeUnit,
} from '~/store/reducers/constants'
import {
  getMemberIsLoading,
  getMembersSelectList,
} from '~/store/reducers/members'
import type { DataHandle, Prescription } from '~/types'

import type { RefillableSectionDataHandle } from '../RefillableSection'
import type { TaskSectionHandle } from '../TaskSection'
import SwapProductCompoundingReason from './SwapProductCompoundingReason'
import SwapProductRefill, {
  type SwapProductRefillHandle,
} from './SwapProductRefill'
import SwapProductTaskDetails from './SwapProductTaskDetails'
import { validateSwapProductForm } from './swapProductValidation'

import style from './SwapProductReactiveForm.module.scss'

const CUSTOM = 'CUSTOM'
const AUTOFILL = 'AUTOFILL'
const MAX_TEAM_MEMBERS = 1000

export interface SwapProductReactiveFormProps {
  currentDoctorHasDvmLicense: boolean | null
  currentDoctorHasMatchingStateLicense: boolean | null
  fields?: {
    automaticallyCreateTask?: Field
    compoundingReason?: Field
    compoundingReasonId?: Field
    doctorId?: Field
    teamMemberId?: Field
  }
  isCompoundingReasonRequired?: boolean
  isRxPrescription?: boolean
  isVetDiet: boolean
  onRefChange?: () => void
  prescription: Prescription
  refillableSectionRef?: RefObject<
    RefillableSectionDataHandle | SwapProductRefillHandle
  >
  showAutomaticallyCreateTaskOption?: boolean
  taskSectionRef?: RefObject<TaskSectionHandle>
}

export interface SwapProductReactiveFormHandle extends DataHandle {
  validate(): boolean
  validate(actionOrOptions: PrescriptionStates | undefined): boolean
}

const SwapProductReactiveForm = forwardRef<
  SwapProductReactiveFormHandle,
  SwapProductReactiveFormProps
>(function SwapProductReactiveForm(
  {
    currentDoctorHasDvmLicense,
    currentDoctorHasMatchingStateLicense,
    fields,
    isCompoundingReasonRequired,
    isRxPrescription = false,
    isVetDiet,
    onRefChange,
    prescription,
    refillableSectionRef,
    showAutomaticallyCreateTaskOption,
    taskSectionRef,
  },
  ref,
) {
  const dispatch = useDispatch()
  const { t } = useTranslation(['Common', 'Dialogs', 'Tooltips', 'Tasks'])
  const doctorValidationMessage = t('Common:SELECT_A_DOCTOR')

  const PrescriptionProductSizeUnit = useSelector(
    getPrescriptionProductSizeUnit,
  )
  const DrugDeliveryMethod = useSelector(getDrugDeliveryMethod)
  const DrugAdministrationFrequencies = useSelector(
    getDrugAdministrationFrequencies,
  )
  const dvmList = useSelector(getMembersSelectList)
  const teamMembers = useTeamMembers(dvmList)
  const rolesList = useMainStaffRoles()
  const isLicenseStateValidationEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FE_DVM_LICENSE_STATE_VALIDATION),
  )

  const isDvmLicenseRequired =
    prescription.prescriptionType === PrescriptionType.CHEWY

  const memberIsLoading = useSelector(getMemberIsLoading)

  const needsDvmLicenseEntry =
    isDvmLicenseRequired &&
    (memberIsLoading || currentDoctorHasDvmLicense === false)

  const canApproveAndSubmitChewyRx =
    useCanApproveAndSubmitChewyActiveRx(needsDvmLicenseEntry)

  const getIsCompoundingReasonDetailsRequired =
    useGetIsCompoundingReasonDetailsRequired()
  const isCompoundingReasonDetailsRequired = useMemo(
    () =>
      isCompoundingReasonRequired && fields?.compoundingReasonId?.value
        ? getIsCompoundingReasonDetailsRequired(
            fields.compoundingReasonId.value,
          )
        : false,
    [
      isCompoundingReasonRequired,
      fields?.compoundingReasonId?.value,
      getIsCompoundingReasonDetailsRequired,
    ],
  )

  const [showValidationError, setShowValidationError] = useState(false)

  const [quantity, setQuantity] = useState<number | undefined>(
    prescription.administrationQuantity ?? undefined,
  )
  const [unit, setUnit] = useState(
    prescription.administrationQuantityUnitId || '',
  )
  const [route, setRoute] = useState(prescription.administrationRouteId || '')
  const [frequency, setFrequency] = useState(
    prescription.administrationFrequencyId || '',
  )
  const [lengthOfCourse, setLengthOfCourse] = useState<
    AdministrationCourseType | undefined
  >(prescription.administrationCourseType || AdministrationCourseType.CUSTOM)
  const [days, setDays] = useState<number | undefined>(
    prescription.administrationCourseLength ?? undefined,
  )
  const [instructionsType, setInstructionsType] = useState<string>(
    prescription.administrationInstructionsAutofill ? AUTOFILL : CUSTOM,
  )
  const [instructionsText, setInstructionsText] = useState<string>(
    prescription.notes || '',
  )

  const requiredAsterisk = isRxPrescription ? (
    <span className={style.requiredAsterisk}>*</span>
  ) : null

  const formRef = useRef<HTMLFormElement>(null)
  // Used for DECLINED state validation to set custom validity on doctor field
  const doctorFieldRef = useRef<HTMLSelectElement>(null)
  const instructionsTextareaRef = useRef<HTMLTextAreaElement>(null)
  const hasFetchedMembersRef = useRef(false)

  const autofilledInstructions = usePrescriptionAdminInstructions({
    administrationQuantity: quantity,
    administrationRouteId: route,
    administrationFrequencyId: frequency,
    administrationQuantityUnitId: unit,
    administrationCourseLength: days,
    administrationCourseType: lengthOfCourse,
    variation: prescription.variation,
  })

  useEffect(() => {
    if (instructionsType === AUTOFILL) {
      setInstructionsText(autofilledInstructions)
      // Clear browser validation state when autofill populates the field
      if (instructionsTextareaRef.current && autofilledInstructions) {
        const textarea = instructionsTextareaRef.current
        textarea.setCustomValidity('')
        // Programmatically focus and blur to trigger browser validation update
        textarea.focus()
        textarea.blur()
      }
    }
  }, [instructionsType, autofilledInstructions])

  useEffect(() => {
    if (hasFetchedMembersRef.current) return

    const roleIds = [RoleName.Veterinarian, RoleName.Veterinarian2].flatMap(
      (roleName) => Utils.findConstantIdsByName(roleName, rolesList),
    )

    if (roleIds.length > 0) {
      hasFetchedMembersRef.current = true
      dispatch(
        fetchMembersForSelectList({
          from: 0,
          to: MAX_TEAM_MEMBERS,
          includeInactive: false,
          shorten: true,
          roleIds,
          noRolesInResult: true,
        }),
      )
    }

    // Intentionally empty: should only run once on mount. rolesList is expected to be populated on mount.
    // dispatch is stable and doesn't need to be in dependencies.
  }, [])

  const getAdministrationFields = () => ({
    administrationQuantity: quantity,
    administrationQuantityUnitId: unit,
    administrationRouteId: route,
    administrationFrequencyId: frequency,
    administrationCourseType: lengthOfCourse,
    administrationCourseLength:
      lengthOfCourse === AdministrationCourseType.CUSTOM ? days : undefined,
    notes: instructionsText,
    administrationInstructionsAutofill:
      instructionsType === AUTOFILL && Boolean(instructionsText),
    ...(isCompoundingReasonRequired
      ? {
          compoundingReasonId: fields?.compoundingReasonId?.value || null,
          compoundingReason: isCompoundingReasonDetailsRequired
            ? fields?.compoundingReason?.value || null
            : null,
        }
      : {}),
  })

  useImperativeHandle(
    ref,
    () => ({
      get: getAdministrationFields,
      validate: (actionOrOptions?: PrescriptionStates | undefined) =>
        validateSwapProductForm(actionOrOptions, {
          formRef,
          doctorFieldRef,
          doctorIdValue: fields?.doctorId?.value,
          doctorValidationMessage,
          isRxPrescription,
          isVetDiet,
          instructionsText,
          refillableSectionRef: refillableSectionRef || null,
          taskSectionRef: taskSectionRef || null,
          automaticallyCreateTaskValue: fields?.automaticallyCreateTask?.value,
          isCompoundingReasonRequired,
          isCompoundingReasonDetailsRequired,
          compoundingReasonIdField: fields?.compoundingReasonId,
          compoundingReasonField: fields?.compoundingReason,
          setShowValidationError,
        }),
    }),
    [
      quantity,
      unit,
      route,
      frequency,
      lengthOfCourse,
      days,
      instructionsText,
      instructionsType,
      fields?.doctorId?.value,
      fields?.automaticallyCreateTask?.value,
      fields?.compoundingReasonId?.value,
      fields?.compoundingReason?.value,
      fields?.compoundingReasonId?.valid,
      fields?.compoundingReason?.valid,
      isRxPrescription,
      isVetDiet,
      isCompoundingReasonRequired,
      isCompoundingReasonDetailsRequired,
      doctorValidationMessage,
      refillableSectionRef,
      taskSectionRef,
      setShowValidationError,
      getIsCompoundingReasonDetailsRequired,
    ],
  )

  return (
    <div className={style.prescriptionBody}>
      <div className={style.topSection}>
        <PrescriptionChewyOrderInfo displayAsRow prescription={prescription} />
        <QuantityAndRefills isVetDiet={isVetDiet} prescription={prescription} />
        <SwapProductButtons
          disabled={!canApproveAndSubmitChewyRx}
          prescription={prescription}
        />
        {showValidationError && (
          <TaskFormValidationErrorToast
            onClose={() => setShowValidationError(false)}
          />
        )}
      </div>
      <form ref={formRef}>
        {isCompoundingReasonRequired &&
          fields?.compoundingReason &&
          fields?.compoundingReasonId && (
            <div className={style.compoundingSection}>
              <SwapProductCompoundingReason
                compoundingReasonField={fields.compoundingReason}
                compoundingReasonIdField={fields.compoundingReasonId}
              />
            </div>
          )}
        <div className={style.adminSection}>
          {!isVetDiet && (
            <>
              <div className={style.adminSectionHeader}>
                {t('Dialogs:PRESCRIPTION_DIALOG.ADMINISTRATION_INSTRUCTIONS')}
              </div>
              <div className={style.adminSectionSplitItems}>
                <KibField
                  required
                  label={t('Common:INSTRUCTION_QUANTITY')}
                  min="0.01"
                  placeholder={t('Common:QTY')}
                  step="any"
                  type="number"
                  value={quantity ?? ''}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const { value } = e.target
                    setQuantity(
                      value === '' ? undefined : Number.parseFloat(value),
                    )
                    setShowValidationError(false)
                  }}
                />
                <KibSelect
                  required
                  className={classNames({ [style.placeholderColor]: !unit })}
                  label={t('Common:UNIT')}
                  value={unit}
                  onChange={(e) => {
                    setUnit(e.target.value)
                    setShowValidationError(false)
                  }}
                >
                  <option disabled hidden value="">
                    {t('Common:UNIT')}
                  </option>
                  {PrescriptionProductSizeUnit.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nameTranslation}
                    </option>
                  ))}
                </KibSelect>
                <KibSelect
                  required
                  className={classNames({ [style.placeholderColor]: !route })}
                  label={t('Common:ROUTE_OF_DRUG_ADMINISTRATION')}
                  value={route}
                  onChange={(e) => {
                    setRoute(e.target.value)
                    setShowValidationError(false)
                  }}
                >
                  <option disabled hidden value="">
                    {t('Common:ROUTE_OF_DRUG_ADMINISTRATION')}
                  </option>
                  {DrugDeliveryMethod.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.nameTranslation}
                    </option>
                  ))}
                </KibSelect>
                <KibSelect
                  required
                  className={classNames({
                    [style.placeholderColor]: !frequency,
                  })}
                  label={t('Common:FREQUENCY_ONE')}
                  value={frequency}
                  onChange={(e) => {
                    setFrequency(e.target.value)
                    setShowValidationError(false)
                  }}
                >
                  <option disabled hidden value="">
                    {t('Common:FREQUENCY_ONE')}
                  </option>
                  {DrugAdministrationFrequencies.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.nameTranslation}
                    </option>
                  ))}
                </KibSelect>
              </div>
              <div className={style.adminLengthOfCourse}>
                <KibInputGroup
                  label={
                    <>
                      {t('Dialogs:PRESCRIPTION_DIALOG.PERIOD_OF_TIME')}
                      {requiredAsterisk}
                    </>
                  }
                >
                  <div className={style.radioChoices}>
                    <KibRadio
                      className={style.dayRadio}
                      label={
                        <span className={style.dayInput}>
                          <KibField
                            key={lengthOfCourse}
                            label={t('Constants:TIME_UNITS.DAY_OTHER')}
                            required={
                              lengthOfCourse === AdministrationCourseType.CUSTOM
                            }
                            value={days ?? ''}
                            onChange={(e) => {
                              const { value } = e.target
                              setDays(
                                value === ''
                                  ? undefined
                                  : Number.parseInt(value, 10),
                              )
                              setShowValidationError(false)
                            }}
                          />
                        </span>
                      }
                      selectedValue={lengthOfCourse}
                      value={AdministrationCourseType.CUSTOM}
                      onChange={() =>
                        setLengthOfCourse(AdministrationCourseType.CUSTOM)
                      }
                    />
                    <KibRadio
                      label={AdministrationCourseTypeStr.UNTIL_GONE}
                      selectedValue={lengthOfCourse}
                      value={AdministrationCourseType.UNTIL_GONE}
                      onChange={() =>
                        setLengthOfCourse(AdministrationCourseType.UNTIL_GONE)
                      }
                    />
                    <KibRadio
                      label={AdministrationCourseTypeStr.AS_NEEDED}
                      selectedValue={lengthOfCourse}
                      value={AdministrationCourseType.AS_NEEDED}
                      onChange={() =>
                        setLengthOfCourse(AdministrationCourseType.AS_NEEDED)
                      }
                    />
                  </div>
                </KibInputGroup>
              </div>
            </>
          )}
          <div className={style.administrationInstructions}>
            {isVetDiet ? (
              <KibInputGroup
                className={style.vetDietInstructions}
                label={
                  <span className={style.detailsLabel}>
                    {t('Common:DETAILS')}
                    <span className={style.requiredAsterisk}>*</span>
                  </span>
                }
              >
                <KibTextarea
                  hiddenLabel
                  required
                  label={t('Common:DETAILS')}
                  placeholder={t('Common:INSTRUCTIONS')}
                  rows={2}
                  value={instructionsText}
                  onChange={(e) => {
                    setInstructionsText(e.target.value)
                    setShowValidationError(false)
                  }}
                />
                <div className={style.customHelpText}>
                  {t('Dialogs:PRESCRIPTION_DIALOG.NOTES_HELPER_TEXT', {
                    notesLength: instructionsText.length || 0,
                  })}
                </div>
              </KibInputGroup>
            ) : (
              <>
                <KibInputGroup
                  label={
                    <>
                      {t('Common:INSTRUCTION_PRINTED_ON_LABEL')}
                      {requiredAsterisk}
                    </>
                  }
                >
                  <div className={style.radioChoices}>
                    <KibRadio
                      label={t('Common:WRITE_MY_OWN_PRESCRIPTION')}
                      selectedValue={instructionsType}
                      value={CUSTOM}
                      onChange={() => setInstructionsType(CUSTOM)}
                    />
                    <KibRadio
                      label={t('Dialogs:PRESCRIPTION_DIALOG.AUTOFILL')}
                      selectedValue={instructionsType}
                      value={AUTOFILL}
                      onChange={() => setInstructionsType(AUTOFILL)}
                    />
                  </div>
                </KibInputGroup>
                <Tooltip
                  enterDelay={300}
                  placement="top-end"
                  title={
                    instructionsType === AUTOFILL
                      ? t('Tooltips:CLICK_WRITE_MY_OWN_TO_EDIT')
                      : ''
                  }
                >
                  <>
                    <KibTextarea
                      hiddenLabel
                      label={t('Common:INSTRUCTIONS')}
                      placeholder={
                        instructionsType === AUTOFILL
                          ? ''
                          : t('Common:INSTRUCTIONS')
                      }
                      readOnly={instructionsType === AUTOFILL}
                      ref={instructionsTextareaRef}
                      required={
                        isRxPrescription && instructionsType !== AUTOFILL
                      }
                      rows={instructionsType === AUTOFILL ? 1 : 2}
                      value={instructionsText}
                      onChange={(e) => {
                        setInstructionsText(e.target.value)
                        setShowValidationError(false)
                      }}
                    />
                    {instructionsType === CUSTOM && (
                      <div className={style.customHelpText}>
                        {t('Dialogs:PRESCRIPTION_DIALOG.NOTES_HELPER_TEXT', {
                          notesLength: instructionsText.length || 0,
                        })}
                      </div>
                    )}
                  </>
                </Tooltip>
              </>
            )}
          </div>
        </div>

        <div className={style.refillSection}>
          <SwapProductRefill
            prescription={prescription}
            ref={refillableSectionRef}
            onRefChange={onRefChange}
          />
        </div>

        {fields?.doctorId && (
          <div className={style.selectMembers}>
            <KibSelect
              required
              className={classNames({
                [style.placeholderColor]: !fields.doctorId.value,
              })}
              label={t('Common:DOCTOR')}
              name="doctorId"
              ref={doctorFieldRef}
              value={fields.doctorId.value}
              onChange={(e) => {
                fields.doctorId?.setValue(e.target.value)
                setShowValidationError(false)
                if (doctorFieldRef.current) {
                  doctorFieldRef.current.setCustomValidity('')
                }
              }}
            >
              <option disabled hidden value="">
                {t('Common:SELECT_A_DOCTOR')}
              </option>
              {teamMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {`${member.firstName} ${member.lastName}`}
                </option>
              ))}
            </KibSelect>
          </div>
        )}

        {isDvmLicenseRequired && (
          <>
            <DoctorLicenseWarning
              currentDoctorHasDvmLicense={currentDoctorHasDvmLicense}
              doctorId={fields?.doctorId?.value}
            />
            {isLicenseStateValidationEnabled &&
              currentDoctorHasDvmLicense === true && (
                <DoctorLicenseStateWarning
                  doctorId={fields?.doctorId?.value}
                  hasMatchingStateLicense={currentDoctorHasMatchingStateLicense}
                />
              )}
          </>
        )}

        {fields?.teamMemberId && (
          <div className={style.teamMemberSection}>
            <div className={style.sectionLabel}>
              {t('Tasks:TASK_DETAILS.TASK_DETAILS')}
            </div>
            <div className={style.selectMembers}>
              <UserSelect
                emptyLabel={t('Common:ASSIGN_TEAM_MEMBER')}
                field={fields.teamMemberId}
                label={t('Common:ASSIGN_TEAM_MEMBER')}
              />
            </div>
          </div>
        )}

        {showAutomaticallyCreateTaskOption &&
          fields?.automaticallyCreateTask && (
            <div className={style.taskSection}>
              <KibCheckbox
                checked={fields.automaticallyCreateTask.value}
                label={t(
                  'Dialogs:PRESCRIPTION_DIALOG.AUTOMATICALLY_CREATE_TASK',
                )}
                onChange={(checked) =>
                  fields.automaticallyCreateTask?.setValue(checked as boolean)
                }
              />
              {fields.automaticallyCreateTask.value && taskSectionRef && (
                <SwapProductTaskDetails
                  prescription={prescription}
                  ref={taskSectionRef}
                  onRefChange={onRefChange}
                />
              )}
            </div>
          )}
      </form>
    </div>
  )
})

export default SwapProductReactiveForm
